import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Upload, FileSpreadsheet, Download } from "lucide-react";
import { useDataImports } from "@/hooks/useDataImports";
import { useSubscriptionLimits } from "@/hooks/useSubscriptionLimits";
import { ImportHistoryTable } from "@/components/import/ImportHistoryTable";
import { ConflictResolutionModal } from "@/components/import/ConflictResolutionModal";
import { PlanLimitTooltip } from "@/components/ui/plan-limit-tooltip";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import * as XLSX from 'xlsx';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const Import = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [conflictImportId, setConflictImportId] = useState<string | null>(null);
  const [conflicts, setConflicts] = useState<any[]>([]);

  useEffect(() => {
    document.title = 'Dashi | Importação';
  }, []);
  const { imports, isLoading, uploadFile } = useDataImports();
  const { bulkImportEnabled, plan } = useSubscriptionLimits();
  const queryClient = useQueryClient();

  // Realtime subscription for imports
  useEffect(() => {
    const channel = supabase
      .channel('imports-updates')
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'data_imports',
      }, () => {
        queryClient.invalidateQueries({ queryKey: ['data-imports'] });
      })
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [queryClient]);

  const handleDownloadTemplate = () => {
    const templateData = [
      {
        tipo_metrica: 'MRR',
        valor: 25000.00,
        data_inicio: '2025-01-01',
        data_fim: '2025-01-31',
        nome_cliente: 'João Silva',
        email_cliente: 'joao@email.com',
        documento_cliente: '123.456.789-00',
        moeda: 'BRL',
        nome_produto: 'Curso Premium',
        id_transacao: '',
      },
      {
        tipo_metrica: 'Churn',
        valor: 2.5,
        data_inicio: '2025-01-01',
        data_fim: '2025-01-31',
        nome_cliente: 'Maria Santos',
        email_cliente: 'maria@email.com',
        documento_cliente: '987.654.321-00',
        moeda: 'BRL',
        nome_produto: 'Curso Premium',
        id_transacao: '',
      },
      {
        tipo_metrica: 'CAC',
        valor: 150.00,
        data_inicio: '2025-01-01',
        data_fim: '2025-01-31',
        nome_cliente: 'Pedro Oliveira',
        email_cliente: 'pedro@email.com',
        documento_cliente: '456.789.123-00',
        moeda: 'BRL',
        nome_produto: 'Curso Premium',
        id_transacao: '',
      },
      {
        tipo_metrica: 'LTV',
        valor: 1200.00,
        data_inicio: '2025-01-01',
        data_fim: '2025-01-31',
        nome_cliente: 'Ana Costa',
        email_cliente: 'ana@email.com',
        documento_cliente: '321.654.987-00',
        moeda: 'BRL',
        nome_produto: 'Curso Premium',
        id_transacao: '',
      },
    ];

    const worksheet = XLSX.utils.json_to_sheet(templateData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Métricas');

    // Set column widths
    worksheet['!cols'] = [
      { wch: 15 }, // tipo_metrica
      { wch: 12 }, // valor
      { wch: 15 }, // data_inicio
      { wch: 15 }, // data_fim
      { wch: 20 }, // nome_cliente
      { wch: 25 }, // email_cliente
      { wch: 18 }, // documento_cliente
      { wch: 8 },  // moeda
      { wch: 20 }, // nome_produto
      { wch: 15 }, // id_transacao
    ];

    XLSX.writeFile(workbook, 'template_importacao_metricas.xlsx');
    toast.success('Template baixado com sucesso!');
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    
    if (!file) {
      return;
    }

    // Validations
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Formato inválido. Use apenas arquivos .xlsx ou .xls');
      return;
    }

    if (file.size > 10 * 1024 * 1024) { // 10MB
      toast.error('Arquivo muito grande. Tamanho máximo: 10MB');
      return;
    }

    setSelectedFile(file);
  };

  const handleFileUpload = async () => {
    if (!selectedFile) {
      toast.error('Selecione um arquivo primeiro');
      return;
    }

    setIsUploading(true);
    try {
      await uploadFile.mutateAsync(selectedFile);
      setSelectedFile(null);
      // Reset input
      const input = document.getElementById('file') as HTMLInputElement;
      if (input) input.value = '';
    } catch (error) {
      console.error('Upload error:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleResolveConflicts = async (importId: string) => {
    try {
      const { data, error } = await supabase
        .from('data_imports_conflicts')
        .select('*')
        .eq('import_id', importId)
        .eq('resolution', 'pending');

      if (error) throw error;

      setConflicts(data || []);
      setConflictImportId(importId);
    } catch (error: any) {
      toast.error('Erro ao carregar conflitos: ' + error.message);
    }
  };

  return (
    <TooltipProvider>
      <div className="container mx-auto py-6 space-y-6">
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Upload className="h-8 w-8" />
            Importar Dados
          </h1>
          <p className="text-muted-foreground mt-1">
            Importe suas métricas através de planilhas Excel
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Upload Card */}
          <Card className={`p-6 relative ${!bulkImportEnabled && 'opacity-50'}`}>
            {!bulkImportEnabled && (
              <div className="absolute inset-0 flex items-center justify-center bg-background/80 backdrop-blur-sm rounded-lg z-10">
                <div className="text-center p-6">
                  <p className="font-semibold text-lg">Importação não disponível</p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Esta funcionalidade não está ativa no plano {plan?.name || 'atual'}
                  </p>
                </div>
              </div>
            )}
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-primary/10 p-3">
                  <Upload className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold">Upload de Arquivo</h3>
                  <p className="text-sm text-muted-foreground">
                    Envie sua planilha de dados
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="file">Selecione o arquivo</Label>
                  <Input
                    id="file"
                    type="file"
                    accept=".xlsx,.xls"
                    className="cursor-pointer"
                    onChange={handleFileSelect}
                    disabled={isUploading}
                  />
                  <p className="text-xs text-muted-foreground">
                    Formatos aceitos: XLSX, XLS (até 10MB)
                  </p>
                  {selectedFile && (
                    <p className="text-sm text-primary font-medium">
                      Arquivo selecionado: {selectedFile.name}
                    </p>
                  )}
                </div>

                <PlanLimitTooltip
                  enabled={bulkImportEnabled}
                  planName={plan?.name || 'atual'}
                  limitType="feature"
                  featureName="Importação em Massa"
                >
                  <Button 
                    className="w-full" 
                    onClick={handleFileUpload}
                    disabled={!selectedFile || isUploading || !bulkImportEnabled}
                  >
                    {isUploading ? (
                      <>
                        <Upload className="mr-2 h-4 w-4 animate-pulse" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        <Upload className="mr-2 h-4 w-4" />
                        Fazer Upload
                      </>
                    )}
                  </Button>
                </PlanLimitTooltip>
              </div>
            </div>
          </Card>

          {/* Template Card */}
          <Card className="p-6">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-success/10 p-3">
                  <FileSpreadsheet className="h-6 w-6 text-success" />
                </div>
                <div>
                  <h3 className="font-semibold">Modelo de Planilha</h3>
                  <p className="text-sm text-muted-foreground">
                    Use nosso template padrão
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="rounded-lg bg-muted p-4 space-y-2">
                  <p className="text-sm font-medium">O template inclui:</p>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• Campos para todas as métricas (MRR, Churn, CAC, LTV)</li>
                    <li>• Dados do cliente (nome, email, documento)</li>
                    <li>• Formatação pré-definida</li>
                    <li>• Exemplos de preenchimento</li>
                  </ul>
                </div>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="outline" className="w-full" onClick={handleDownloadTemplate}>
                      <Download className="mr-2 h-4 w-4" />
                      Baixar Template
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Baixar modelo de planilha para importação</p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>
          </Card>
        </div>

        {/* Instructions */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Como importar seus dados</h3>
          <ol className="space-y-3 text-sm text-muted-foreground">
            <li className="flex gap-3">
              <span className="font-semibold text-primary">1.</span>
              Baixe o template de planilha e preencha com seus dados
            </li>
            <li className="flex gap-3">
              <span className="font-semibold text-primary">2.</span>
              <strong>nome_cliente</strong> é obrigatório. Email e documento são opcionais mas ajudam a evitar duplicatas
            </li>
            <li className="flex gap-3">
              <span className="font-semibold text-primary">3.</span>
              Se <strong>nome_produto</strong> for informado, o produto deve estar cadastrado no sistema
            </li>
            <li className="flex gap-3">
              <span className="font-semibold text-primary">4.</span>
              Faça o upload do arquivo e aguarde o processamento
            </li>
            <li className="flex gap-3">
              <span className="font-semibold text-primary">5.</span>
              Se houver conflitos (clientes com mesmo email mas nome diferente), você será notificado para resolver
            </li>
          </ol>
        </Card>

        {/* Import History */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Histórico de Importações</h3>
          {isLoading ? (
            <div className="text-center text-muted-foreground py-8">Carregando...</div>
          ) : (
            <ImportHistoryTable 
              imports={imports || []} 
              onResolveConflicts={handleResolveConflicts}
            />
          )}
        </Card>
      </div>

      {/* Conflict Resolution Modal */}
      <ConflictResolutionModal
        open={conflictImportId !== null}
        onOpenChange={(open) => !open && setConflictImportId(null)}
        importId={conflictImportId || ''}
        conflicts={conflicts}
        onResolved={() => {
          queryClient.invalidateQueries({ queryKey: ['data-imports'] });
          setConflictImportId(null);
          setConflicts([]);
        }}
      />
    </div>
    </TooltipProvider>
  );
};

export default Import;
